// Journey Detail Page Component
// ==============================
// Feature #256: Updated to use i18n translations
// Feature #290: Updated with new design system - frame styling, typography

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLearningJourneys } from '../../hooks/useLearningJourneys';
import { useJourneyTopics } from '../../hooks/useJourneyTopics';
import Button from '../ui/Button';
import Input from '../ui/Input';
import Breadcrumb, { BreadcrumbItem } from '../ui/Breadcrumb';
import Layout from '../layout/Layout';
import { useTranslation } from 'react-i18next';
import type { LearningJourney, ExamFormat, Subtopic } from '../../types';

// Extended ExamFormat type for the edit modal (includes empty string option)
type EditExamFormat = ExamFormat | '';

// Helper to calculate days until exam
const getDaysUntilExam = (examDate: Date | string | undefined): number => {
  if (!examDate) return -1;
  const exam = examDate instanceof Date ? examDate : new Date(examDate);
  exam.setHours(0, 0, 0, 0);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diffTime = exam.getTime() - today.getTime();
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return diffDays;
};

// formatCountdown is now handled inline using i18n

const JourneyDetailPage: React.FC = () => {
  const { t } = useTranslation('library');
  const { t: tCommon } = useTranslation('common');
  const navigate = useNavigate();
  const { journeyId } = useParams<{ journeyId: string }>();
  const { user } = useAuth();

  // Use centralized learning journeys hook (Feature #21 - Firebase CRUD)
  const {
    getJourney,
    updateJourney,
    deleteJourney: deleteJourneyFromFirebase,
  } = useLearningJourneys();

  // Use Firebase-backed topics hook (Feature #91 - Fix localStorage/Firebase inconsistency)
  const {
    topics,
    loading: topicsLoading,
    removeTopic,
  } = useJourneyTopics(journeyId);

  const [journey, setJourney] = useState<LearningJourney | null>(null);
  const [loading, setLoading] = useState(true);

  // Delete confirmation state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Edit modal state
  const [showEditModal, setShowEditModal] = useState(false);
  const [editSubject, setEditSubject] = useState('');
  const [editExamDate, setEditExamDate] = useState('');
  const [editExamFormat, setEditExamFormat] = useState<EditExamFormat>('');
  const [isSaving, setIsSaving] = useState(false);
  const [editError, setEditError] = useState<string | null>(null);

  // Storage key for sessions (still using localStorage for session history)
  const getSessionsKey = useCallback(() => {
    return user?.uid ? `lyra_sessions_${user.uid}` : 'lyra_sessions';
  }, [user?.uid]);

  // Delete journey and clean up related data (Feature #21 - Firebase CRUD)
  // Updated in Feature #91 to use Firebase for topics instead of localStorage
  const handleDeleteJourney = async () => {
    if (deleteConfirmText !== t('journeyDetail.deleteConfirmText') || !journeyId) {
      setDeleteError(t('journeyDetail.deleteConfirmError'));
      return;
    }

    setIsDeleting(true);
    setDeleteError(null);

    try {
      const sessionsKey = getSessionsKey();

      // 1. Delete all topics associated with this journey from Firebase
      // Topics are stored as subcollection under the journey, so we delete them first
      const topicIds = topics.map(t => t.id);
      for (const topicId of topicIds) {
        await removeTopic(topicId);
      }

      // 2. Delete the journey using Firebase CRUD (Feature #21)
      const result = await deleteJourneyFromFirebase(journeyId);
      if (!result.success) {
        setDeleteError(result.error || t('journeyDetail.couldNotDelete', { journey: tCommon('learningJourney.singularLower') }));
        setIsDeleting(false);
        return;
      }

      // 3. Archive or mark sessions related to this journey's topics
      // (Sessions are kept for history but we mark them as from a deleted journey)
      const savedSessions = localStorage.getItem(sessionsKey);
      if (savedSessions) {
        const allSessions = JSON.parse(savedSessions);
        const updatedSessions = allSessions.map((s: { topicId: string; deletedJourneyId?: string }) =>
          topicIds.includes(s.topicId) ? { ...s, deletedJourneyId: journeyId } : s
        );
        localStorage.setItem(sessionsKey, JSON.stringify(updatedSessions));
      }

      // Navigate back to teaching page
      navigate('/sessions');
    } catch (err) {
      setDeleteError(t('journeyDetail.couldNotDeleteRetry', { journey: tCommon('learningJourney.singularLower') }));
      setIsDeleting(false);
    }
  };

  // Open edit modal with current journey data
  const handleOpenEditModal = () => {
    if (journey) {
      setEditSubject(journey.subject || journey.title || '');
      // Handle examDate which can be Date, string, or undefined
      const examDateString = journey.examDate
        ? (journey.examDate instanceof Date
          ? journey.examDate.toISOString().split('T')[0]
          : typeof journey.examDate === 'string' && journey.examDate.includes('T')
            ? journey.examDate.split('T')[0]
            : journey.examDate as string)
        : '';
      setEditExamDate(examDateString);
      setEditExamFormat(journey.examFormat || '');
      setEditError(null);
      setShowEditModal(true);
    }
  };

  // Save edited journey (Feature #21 - Firebase CRUD)
  const handleSaveEdit = async () => {
    if (!editSubject.trim() || !journeyId) {
      setEditError(t('journeyDetail.subjectRequired'));
      return;
    }

    setIsSaving(true);
    setEditError(null);

    try {
      // Use Firebase CRUD to update journey
      const result = await updateJourney(journeyId, {
        subject: editSubject.trim(),
        examDate: editExamDate || undefined,
        examFormat: editExamFormat || undefined,
      });

      if (!result.success) {
        setEditError(result.error || t('journeyDetail.couldNotSave'));
        return;
      }

      // Update local state
      setJourney(prev => prev ? {
        ...prev,
        subject: editSubject.trim(),
        examDate: editExamDate,
        examFormat: editExamFormat || undefined,
      } : null);

      setShowEditModal(false);
    } catch (err) {
      setEditError(t('journeyDetail.couldNotSaveRetry'));
    } finally {
      setIsSaving(false);
    }
  };

  // Close edit modal and reset state
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setEditError(null);
  };

  // Load journey data (Feature #21 - Firebase CRUD for journeys)
  // Topics are now loaded via useJourneyTopics hook (Feature #91)
  useEffect(() => {
    const loadData = async () => {
      if (!journeyId) {
        setLoading(false);
        return;
      }

      setLoading(true);

      // Load journey using Firebase CRUD
      const journeyData = await getJourney(journeyId);
      setJourney(journeyData);

      // Topics are automatically loaded by useJourneyTopics hook
      setLoading(false);
    };

    loadData();
  }, [journeyId, getJourney]);

  // Build breadcrumbs
  const breadcrumbItems: BreadcrumbItem[] = [
    { label: tCommon('nav.myJourneys'), href: '/sessions' },
    { label: journey?.subject || tCommon('learningJourney.singular') },
  ];

  // Show loading state while journey or topics are loading
  if (loading || topicsLoading) {
    return (
      <div className="min-h-screen bg-background-light dark:bg-background-dark flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500"></div>
      </div>
    );
  }

  if (!journey) {
    return (
      <div className="min-h-screen bg-background-light dark:bg-background-dark">
        <header className="bg-canvas dark:bg-canvas-dark border-b border-frame-border dark:border-frame-border-dark">
          <div className="max-w-4xl mx-auto px-4 py-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/sessions')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
                aria-label={tCommon('actions.back')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 text-neutral-dark dark:text-white" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                </svg>
              </button>
              {/* Feature #280: Serif display font for headings */}
              <h1 className="text-2xl font-display font-bold text-neutral-dark dark:text-white">
                {t('journeyDetail.journeyNotFound', { journey: tCommon('learningJourney.singular') })}
              </h1>
            </div>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-4 py-8">
          <div className="text-center py-16 bg-canvas dark:bg-canvas-dark rounded-xl border-2 border-dashed border-frame-border dark:border-frame-border-dark">
            <div className="text-4xl mb-4">🔍</div>
            <p className="text-neutral-medium dark:text-gray-400 mb-6">
              {t('journeyDetail.journeyNotFoundDescription', { journey: tCommon('learningJourney.singularLower') })}
            </p>
            <Button onClick={() => navigate('/sessions')}>
              {t('journeyDetail.backToTeaching')}
            </Button>
          </div>
        </main>
      </div>
    );
  }

  const daysUntil = getDaysUntilExam(journey.examDate);
  // Calculate subtopic totals - subtopics can be Subtopic[] or undefined
  const totalSubtopics = topics.reduce((acc, t) => acc + (t.subtopics?.length || 0), 0);
  const completedSubtopics = topics.reduce((acc, t) => {
    if (!t.subtopics) return acc;
    return acc + t.subtopics.filter((s: Subtopic) => s.isCompleted || s.completed).length;
  }, 0);

  return (
    <Layout>
      {/* Header - Feature #290: Updated with frame styling */}
      <header className="bg-canvas dark:bg-canvas-dark border-b border-frame-border dark:border-frame-border-dark">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/sessions')}
                className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors md:hidden"
                aria-label={t('journeyDetail.backToTeaching')}
              >
                <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-5 h-5 text-neutral-dark dark:text-white" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
                </svg>
              </button>
              {/* Feature #280: Serif display font for headings */}
              <h1 className="text-2xl font-display font-bold text-neutral-dark dark:text-white">
                {journey.subject}
              </h1>
            </div>
            {/* Edit button */}
            <button
              onClick={handleOpenEditModal}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-neutral-medium hover:text-neutral-dark dark:text-gray-400 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors"
              aria-label={t('journeyDetail.editData')}
            >
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-4 h-4" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10" />
              </svg>
              <span className="hidden sm:inline">{t('journeyDetail.editData')}</span>
            </button>
          </div>
          <div className="mt-2">
            <Breadcrumb items={breadcrumbItems} />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        {/* Journey Overview Card - Feature #290: Frame styling */}
        <section className="bg-canvas dark:bg-canvas-dark rounded-xl shadow-sm border border-frame-border dark:border-frame-border-dark p-6">
          <h2 className="text-lg font-semibold text-neutral-dark dark:text-white mb-4">
            {t('journeyDetail.overview')}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Exam Date */}
            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p className="text-sm text-neutral-medium mb-1">{t('journeyDetail.examDate')}</p>
              <p className="text-lg font-semibold text-neutral-dark dark:text-white">
                {journey.examDate
                  ? new Date(journey.examDate).toLocaleDateString('da-DK', {
                      day: 'numeric',
                      month: 'long',
                      year: 'numeric',
                    })
                  : t('journeyDetail.noExamDate')}
              </p>
              {journey.examDate && (
                <p className={`text-sm mt-1 ${
                  daysUntil <= 7 && daysUntil >= 0
                    ? 'text-accent-600 font-medium'
                    : 'text-neutral-medium'
                }`}>
                  {daysUntil < 0 ? t('journeyDetail.examPassed') : daysUntil === 0 ? t('journeyDetail.examToday') : t('journeyDetail.daysLeft', { count: daysUntil })}
                </p>
              )}
            </div>

            {/* Topics Count */}
            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p className="text-sm text-neutral-medium mb-1">{tCommon('topic.plural')}</p>
              <p className="text-lg font-semibold text-neutral-dark dark:text-white">
                {t('journeyDetail.topicCount', { count: topics.length })}
              </p>
              <p className="text-sm text-neutral-medium mt-1">
                {t('journeyDetail.subtopicCount', { count: totalSubtopics })}
              </p>
            </div>

            {/* Progress */}
            <div className="p-4 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p className="text-sm text-neutral-medium mb-1">{t('journeyDetail.progress')}</p>
              <p className="text-lg font-semibold text-neutral-dark dark:text-white">
                {totalSubtopics > 0 ? Math.round((completedSubtopics / totalSubtopics) * 100) : 0}%
              </p>
              <p className="text-sm text-neutral-medium mt-1">
                {t('journeyDetail.completed', { completed: completedSubtopics, total: totalSubtopics })}
              </p>
            </div>
          </div>

          {/* Creation Info */}
          <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
            <p className="text-sm text-neutral-medium">
              {t('journeyDetail.createdAt')} {new Date(journey.createdAt).toLocaleDateString('da-DK', {
                day: 'numeric',
                month: 'long',
                year: 'numeric',
              })}
            </p>
          </div>
        </section>

        {/* Topics in Journey - Feature #290: Frame styling */}
        <section className="bg-canvas dark:bg-canvas-dark rounded-xl shadow-sm border border-frame-border dark:border-frame-border-dark p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-neutral-dark dark:text-white">
              {t('journeyDetail.topicsInJourney')}
            </h2>
            <Button variant="outline" size="sm" onClick={() => navigate(`/library/${journeyId}`)}>
              {t('journeyDetail.viewAllTopics')}
            </Button>
          </div>

          {topics.length === 0 ? (
            <div className="text-center py-8">
              <div className="text-3xl mb-3">📚</div>
              <p className="text-neutral-medium dark:text-gray-400 mb-4">
                {t('journeyDetail.noTopicsLinked')}
              </p>
              <Button variant="outline" onClick={() => navigate(`/library/journey/${journeyId}`)}>
                {t('journeyDetail.createPlanWithAnna')}
              </Button>
            </div>
          ) : (
            <ul className="space-y-3">
              {topics.map(topic => {
                const subtopicCount = topic.subtopics?.length || 0;
                const completedCount = topic.subtopics?.filter(
                  (s: Subtopic) => s.isCompleted || s.completed
                ).length || 0;
                const progress = subtopicCount > 0
                  ? Math.round((completedCount / subtopicCount) * 100)
                  : 0;

                return (
                  <li key={topic.id}>
                    <button
                      onClick={() => navigate(`/workspace/${journeyId}/${topic.id}`)}
                      className="w-full text-left p-4 bg-gray-50 dark:bg-gray-800 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <p className="font-medium text-neutral-dark dark:text-white">
                          {topic.title}
                        </p>
                        <span className="text-sm text-neutral-medium">
                          {progress}%
                        </span>
                      </div>
                      <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-primary-500 rounded-full transition-all duration-300"
                          style={{ width: `${progress}%` }}
                        />
                      </div>
                      <p className="text-xs text-neutral-medium mt-2">
                        {t('journeyDetail.subtopicsCount', { count: subtopicCount })} · {t('journeyDetail.subtopicsCompleted', { count: completedCount })}
                      </p>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </section>

        {/* Danger Zone - Feature #290: Frame styling with red accent border */}
        <section className="bg-canvas dark:bg-canvas-dark rounded-xl shadow-sm border border-red-200 dark:border-red-900 p-6">
          <h2 className="text-lg font-semibold text-red-600 dark:text-red-400 mb-4">
            {t('journeyDetail.dangerZone')}
          </h2>
          <div className="space-y-4">
            <div>
              <p className="text-neutral-dark dark:text-white font-medium">{t('journeyDetail.deleteJourneyLabel', { journey: tCommon('learningJourney.singularLower') })}</p>
              <p className="text-sm text-neutral-medium mb-3">
                {t('journeyDetail.deleteJourneyDescription', { journey: tCommon('learningJourney.singularLower') })}
              </p>

              {!showDeleteConfirm ? (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="border-red-300 text-red-600 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/20"
                >
                  {t('journeyDetail.deleteThisJourney')}
                </Button>
              ) : (
                <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
                  <p className="text-sm text-red-600 dark:text-red-400 font-medium mb-3">
                    {t('journeyDetail.deleteConfirmPrompt')}
                  </p>

                  {deleteError && (
                    <div
                      className="mb-3 p-2 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded text-red-600 dark:text-red-400 text-sm"
                      role="alert"
                    >
                      {deleteError}
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row gap-3">
                    <div className="flex-1">
                      <label htmlFor="delete-journey-confirm" className="sr-only">
                        {t('journeyDetail.deleteConfirmLabel')}
                      </label>
                      <input
                        id="delete-journey-confirm"
                        type="text"
                        value={deleteConfirmText}
                        onChange={(e) => setDeleteConfirmText(e.target.value.toUpperCase())}
                        placeholder={t('journeyDetail.deleteConfirmPlaceholder')}
                        className="w-full px-3 py-2 border border-red-300 dark:border-red-700 rounded-lg bg-white dark:bg-gray-800 text-neutral-dark dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                        disabled={isDeleting}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setShowDeleteConfirm(false);
                          setDeleteConfirmText('');
                          setDeleteError(null);
                        }}
                        disabled={isDeleting}
                      >
                        {tCommon('actions.cancel')}
                      </Button>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={handleDeleteJourney}
                        disabled={isDeleting}
                        className="bg-red-600 hover:bg-red-700 border-red-600"
                      >
                        {isDeleting ? t('journeyDetail.deleting') : t('journeyDetail.confirmDeletion')}
                      </Button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      {/* Edit Journey Modal */}
      {showEditModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          {/* Feature #290: Modal with frame styling */}
          <div className="bg-canvas dark:bg-canvas-dark rounded-2xl w-full max-w-md shadow-2xl border border-frame-border dark:border-frame-border-dark">
            {/* Header */}
            <div className="p-6 border-b border-frame-border dark:border-frame-border-dark">
              <h2 className="text-xl font-bold text-neutral-dark dark:text-white flex items-center">
                <span className="mr-2">✏️</span>
                {t('journeyDetail.editJourney', { journey: tCommon('learningJourney.singular') })}
              </h2>
              <p className="text-sm text-neutral-medium mt-1">
                {t('journeyDetail.editJourneyDescription', { journey: tCommon('learningJourney.your') })}
              </p>
            </div>

            {/* Content */}
            <div className="p-6 space-y-4">
              {editError && (
                <div
                  className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg"
                  role="alert"
                >
                  <p className="text-red-600 dark:text-red-400 text-sm">{editError}</p>
                </div>
              )}

              {/* Subject/{t('journeyDetail.subjectLabel')} */}
              <div>
                <label htmlFor="edit-subject" className="block text-sm font-medium text-neutral-dark dark:text-white mb-1">
                  {t('journeyDetail.subjectLabel')} *
                </label>
                <Input
                  id="edit-subject"
                  type="text"
                  value={editSubject}
                  onChange={(e) => setEditSubject(e.target.value)}
                  placeholder={t('journeyDetail.subjectPlaceholder')}
                  disabled={isSaving}
                />
              </div>

              {/* Exam Date */}
              <div>
                <label htmlFor="edit-exam-date" className="block text-sm font-medium text-neutral-dark dark:text-white mb-1">
                  {t('journeyDetail.examDateLabel')}
                </label>
                <Input
                  id="edit-exam-date"
                  type="date"
                  value={editExamDate}
                  onChange={(e) => setEditExamDate(e.target.value)}
                  disabled={isSaving}
                />
              </div>

              {/* Exam Format */}
              <div>
                <label className="block text-sm font-medium text-neutral-dark dark:text-white mb-2">
                  {t('journeyDetail.examFormat')}
                </label>
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => setEditExamFormat('mundtlig')}
                    disabled={isSaving}
                    className={`w-full p-3 text-left rounded-lg border-2 transition-colors ${editExamFormat === 'mundtlig'
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                      } ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">🎤</span>
                      <span className="font-medium text-neutral-dark dark:text-white">{t('journeyDetail.oral')}</span>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setEditExamFormat('skriftlig')}
                    disabled={isSaving}
                    className={`w-full p-3 text-left rounded-lg border-2 transition-colors ${editExamFormat === 'skriftlig'
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                      } ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">📝</span>
                      <span className="font-medium text-neutral-dark dark:text-white">{t('journeyDetail.written')}</span>
                    </div>
                  </button>

                  <button
                    type="button"
                    onClick={() => setEditExamFormat('multiple_choice')}
                    disabled={isSaving}
                    className={`w-full p-3 text-left rounded-lg border-2 transition-colors ${editExamFormat === 'multiple_choice'
                        ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300'
                      } ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">✅</span>
                      <span className="font-medium text-neutral-dark dark:text-white">{t('journeyDetail.multipleChoice')}</span>
                    </div>
                  </button>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-frame-border dark:border-frame-border-dark bg-background-light dark:bg-background-dark flex justify-end gap-3 rounded-b-2xl">
              <Button
                variant="outline"
                onClick={handleCloseEditModal}
                disabled={isSaving}
              >
                {tCommon('actions.cancel')}
              </Button>
              <Button
                variant="primary"
                onClick={handleSaveEdit}
                disabled={isSaving}
              >
                {isSaving ? t('journeyDetail.saving') : t('journeyDetail.saveChanges')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default JourneyDetailPage;
